import { useState, useEffect } from 'react';
import {
  Download,
  Users,
  Eye,
  Clock,
  Globe,
  Laptop,
  Smartphone,
  Monitor,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  RefreshCw,
  DollarSign,
  TrendingUp,
  CreditCard,
  Wallet,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getDailyStats,
  getPageViews,
  getDownloads,
  onDailyStatsChange,
  getCurrentMonthRevenue,
  onMonthlyRevenueChange,
  getSubscriptionStats,
  onSubscriptionsChange,
  getAIUsageSummary,
  onAIUsageChange,
  FirestoreDailyStats,
  FirestorePageView,
  FirestoreDownload,
  FirestoreMonthlyRevenue,
  FirestoreMonthlyAIUsage
} from '@/services/firebase/adminFirestore';

interface StatCard {
  label: string;
  value: string;
  change: number;
  trend: 'up' | 'down';
  icon: React.ReactNode;
}

interface PageViewStats {
  total: number;
  today: number;
  thisWeek: number;
  changeFromLastWeek: number;
}

interface DownloadStats {
  total: number;
  changeFromLastWeek: number;
  byPlatform: {
    windows: number;
    macos: number;
    linux: number;
  };
}

interface DeviceBreakdown {
  desktop: number;
  mobile: number;
  tablet: number;
}

interface CountryData {
  country: string;
  views: number;
  percentage: number;
}

interface TopPage {
  path: string;
  views: number;
  uniqueVisitors: number;
}

export function AdminAnalytics() {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Firebase data state
  const [dailyStats, setDailyStats] = useState<FirestoreDailyStats[]>([]);
  const [pageViews, setPageViews] = useState<FirestorePageView[]>([]);
  const [downloads, setDownloads] = useState<FirestoreDownload[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<FirestoreMonthlyRevenue | null>(null);
  const [subscriptionStats, setSubscriptionStats] = useState<{
    total: number;
    active: number;
    cancelled: number;
    trial: number;
    monthlyRecurring: number;
    averageRevenuePerUser: number;
  }>({ total: 0, active: 0, cancelled: 0, trial: 0, monthlyRecurring: 0, averageRevenuePerUser: 0 });
  const [aiUsage, setAiUsage] = useState<FirestoreMonthlyAIUsage[]>([]);

  // Fetch initial data from Firebase
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [dailyStatsData, pageViewsData, downloadsData, revenueData, subsData, aiUsageData] = await Promise.all([
          getDailyStats(90),
          getPageViews(90),
          getDownloads(90),
          getCurrentMonthRevenue(),
          getSubscriptionStats(),
          getAIUsageSummary()
        ]);
        setDailyStats(dailyStatsData);
        setPageViews(pageViewsData);
        setDownloads(downloadsData);
        setMonthlyRevenue(revenueData);
        setSubscriptionStats(subsData);
        setAiUsage(aiUsageData.byModel.map((m, i) => ({
          id: `${i}`,
          month: new Date().toISOString().slice(0, 7),
          modelId: m.modelName.toLowerCase().replace(/\s+/g, '-'),
          modelName: m.modelName,
          provider: m.provider,
          totalRequests: 0,
          totalInputTokens: 0,
          totalOutputTokens: 0,
          totalCost: m.cost,
          updatedAt: new Date()
        })));
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Set up real-time listeners
  useEffect(() => {
    const unsubscribeDailyStats = onDailyStatsChange((stats) => {
      setDailyStats(stats);
    });

    const unsubscribeRevenue = onMonthlyRevenueChange((revenue) => {
      // Get current month's revenue
      const currentMonth = new Date().toISOString().slice(0, 7);
      const current = revenue.find(r => r.month === currentMonth);
      setMonthlyRevenue(current || null);
    });

    const unsubscribeSubscriptions = onSubscriptionsChange((stats) => {
      setSubscriptionStats(stats);
    });

    const unsubscribeAIUsage = onAIUsageChange((usage) => {
      setAiUsage(usage);
    });

    return () => {
      unsubscribeDailyStats();
      unsubscribeRevenue();
      unsubscribeSubscriptions();
      unsubscribeAIUsage();
    };
  }, []);

  // Calculate page view stats from Firebase data
  const calculatePageViewStats = (): PageViewStats => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const twoWeeksAgo = new Date(now);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

    const total = pageViews.length;
    const todayViews = pageViews.filter(pv =>
      pv.timestamp.toISOString().split('T')[0] === today
    ).length;

    const thisWeekViews = pageViews.filter(pv =>
      pv.timestamp >= oneWeekAgo
    ).length;

    const lastWeekViews = pageViews.filter(pv =>
      pv.timestamp >= twoWeeksAgo && pv.timestamp < oneWeekAgo
    ).length;

    const changeFromLastWeek = lastWeekViews > 0
      ? Math.round(((thisWeekViews - lastWeekViews) / lastWeekViews) * 100)
      : 0;

    return {
      total,
      today: todayViews,
      thisWeek: thisWeekViews,
      changeFromLastWeek
    };
  };

  // Calculate download stats from Firebase data
  const calculateDownloadStats = (): DownloadStats => {
    const now = new Date();
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const twoWeeksAgo = new Date(now);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

    const total = downloads.length;

    const thisWeekDownloads = downloads.filter(d =>
      d.timestamp >= oneWeekAgo
    ).length;

    const lastWeekDownloads = downloads.filter(d =>
      d.timestamp >= twoWeeksAgo && d.timestamp < oneWeekAgo
    ).length;

    const changeFromLastWeek = lastWeekDownloads > 0
      ? Math.round(((thisWeekDownloads - lastWeekDownloads) / lastWeekDownloads) * 100)
      : 0;

    const byPlatform = {
      windows: downloads.filter(d => d.platform === 'windows').length,
      macos: downloads.filter(d => d.platform === 'macos').length,
      linux: downloads.filter(d => d.platform === 'linux').length
    };

    return {
      total,
      changeFromLastWeek,
      byPlatform
    };
  };

  // Calculate device breakdown from Firebase data
  const calculateDeviceBreakdown = (): DeviceBreakdown => {
    return {
      desktop: pageViews.filter(pv => pv.device === 'desktop').length,
      mobile: pageViews.filter(pv => pv.device === 'mobile').length,
      tablet: pageViews.filter(pv => pv.device === 'tablet').length
    };
  };

  // Calculate country breakdown from Firebase data
  const calculateCountryBreakdown = (): CountryData[] => {
    const countryMap = new Map<string, number>();

    pageViews.forEach(pv => {
      const country = pv.country || 'Unknown';
      countryMap.set(country, (countryMap.get(country) || 0) + 1);
    });

    const total = pageViews.length;
    const countryData: CountryData[] = [];

    countryMap.forEach((views, country) => {
      countryData.push({
        country,
        views,
        percentage: total > 0 ? Math.round((views / total) * 100) : 0
      });
    });

    return countryData.sort((a, b) => b.views - a.views);
  };

  // Calculate top pages from Firebase data
  const calculateTopPages = (limit: number = 5): TopPage[] => {
    const pageMap = new Map<string, { views: number; uniqueSessions: Set<string> }>();

    pageViews.forEach(pv => {
      if (!pageMap.has(pv.path)) {
        pageMap.set(pv.path, { views: 0, uniqueSessions: new Set() });
      }
      const pageData = pageMap.get(pv.path)!;
      pageData.views++;
      pageData.uniqueSessions.add(pv.sessionId);
    });

    const topPages: TopPage[] = [];

    pageMap.forEach((data, path) => {
      topPages.push({
        path,
        views: data.views,
        uniqueVisitors: data.uniqueSessions.size
      });
    });

    return topPages.sort((a, b) => b.views - a.views).slice(0, limit);
  };

  // Get filtered daily stats based on date range
  const getFilteredDailyStats = (): FirestoreDailyStats[] => {
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
    return dailyStats.slice(-days);
  };

  const pageViewStats = calculatePageViewStats();
  const downloadStats = calculateDownloadStats();
  const topPages = calculateTopPages(5);
  const deviceBreakdown = calculateDeviceBreakdown();
  const countryBreakdown = calculateCountryBreakdown();
  const filteredDailyStats = getFilteredDailyStats();

  // Calculate total device count
  const totalDevices = deviceBreakdown.desktop + deviceBreakdown.mobile + deviceBreakdown.tablet;
  const devicePercentages = {
    desktop: totalDevices > 0 ? Math.round((deviceBreakdown.desktop / totalDevices) * 100) : 0,
    mobile: totalDevices > 0 ? Math.round((deviceBreakdown.mobile / totalDevices) * 100) : 0,
    tablet: totalDevices > 0 ? Math.round((deviceBreakdown.tablet / totalDevices) * 100) : 0,
  };

  // Revenue stats from Firebase (real data)
  const revenueStats = {
    totalRevenue: monthlyRevenue?.totalRevenue || 0,
    totalApiCost: monthlyRevenue?.totalApiCost || 0,
    profit: monthlyRevenue?.profit || 0,
    profitMargin: monthlyRevenue?.profitMargin || 0,
    monthlyRecurring: subscriptionStats.monthlyRecurring,
    activeSubscriptions: subscriptionStats.active,
    averageRevenuePerUser: subscriptionStats.averageRevenuePerUser,
    changeFromLastMonth: 0, // TODO: Calculate from previous month comparison
  };

  // AI Usage stats from Firebase (real data)
  const totalAiCost = aiUsage.reduce((sum, u) => sum + u.totalCost, 0);
  const aiUsageByModel = aiUsage.map(u => ({
    model: u.modelName,
    usage: u.totalCost,
    percentage: totalAiCost > 0 ? Math.round((u.totalCost / totalAiCost) * 100) : 0,
  }));

  const stats: StatCard[] = [
    {
      label: 'Monthly Revenue',
      value: `$${revenueStats.totalRevenue.toLocaleString()}`,
      change: revenueStats.changeFromLastMonth,
      trend: revenueStats.changeFromLastMonth >= 0 ? 'up' : 'down',
      icon: <DollarSign size={20} />
    },
    {
      label: 'Net Profit',
      value: `$${revenueStats.profit.toLocaleString()}`,
      change: revenueStats.profitMargin,
      trend: 'up',
      icon: <TrendingUp size={20} />
    },
    {
      label: 'Active Subscriptions',
      value: revenueStats.activeSubscriptions.toLocaleString(),
      change: 8.3,
      trend: 'up',
      icon: <CreditCard size={20} />
    },
    {
      label: 'ARPU',
      value: `$${revenueStats.averageRevenuePerUser.toFixed(2)}`,
      change: 5.2,
      trend: 'up',
      icon: <Wallet size={20} />
    },
  ];

  const trafficStats: StatCard[] = [
    {
      label: 'Total Downloads',
      value: downloadStats.total.toLocaleString(),
      change: downloadStats.changeFromLastWeek,
      trend: downloadStats.changeFromLastWeek >= 0 ? 'up' : 'down',
      icon: <Download size={20} />
    },
    {
      label: 'Page Views (This Week)',
      value: pageViewStats.thisWeek.toLocaleString(),
      change: pageViewStats.changeFromLastWeek,
      trend: pageViewStats.changeFromLastWeek >= 0 ? 'up' : 'down',
      icon: <Eye size={20} />
    },
    {
      label: 'Total Views',
      value: pageViewStats.total.toLocaleString(),
      change: 0,
      trend: 'up',
      icon: <Users size={20} />
    },
    {
      label: 'Today\'s Views',
      value: pageViewStats.today.toLocaleString(),
      change: 0,
      trend: 'up',
      icon: <Clock size={20} />
    },
  ];

  const trafficByDevice = [
    { label: 'Desktop', value: deviceBreakdown.desktop, percentage: devicePercentages.desktop },
    { label: 'Mobile', value: deviceBreakdown.mobile, percentage: devicePercentages.mobile },
    { label: 'Tablet', value: deviceBreakdown.tablet, percentage: devicePercentages.tablet },
  ];

  // Get last 7 days for the chart
  const last7DaysStats = filteredDailyStats.slice(-7);
  const maxVisits = Math.max(...last7DaysStats.map(d => d.pageViews), 1);

  const getDeviceIcon = (device: string) => {
    switch (device) {
      case 'Desktop': return <Monitor size={16} />;
      case 'Mobile': return <Smartphone size={16} />;
      case 'Tablet': return <Laptop size={16} />;
      default: return <Globe size={16} />;
    }
  };

  const getDayName = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const [dailyStatsData, pageViewsData, downloadsData, revenueData, subsData, aiUsageData] = await Promise.all([
        getDailyStats(90),
        getPageViews(90),
        getDownloads(90),
        getCurrentMonthRevenue(),
        getSubscriptionStats(),
        getAIUsageSummary()
      ]);
      setDailyStats(dailyStatsData);
      setPageViews(pageViewsData);
      setDownloads(downloadsData);
      setMonthlyRevenue(revenueData);
      setSubscriptionStats(subsData);
      setAiUsage(aiUsageData.byModel.map((m, i) => ({
        id: `${i}`,
        month: new Date().toISOString().slice(0, 7),
        modelId: m.modelName.toLowerCase().replace(/\s+/g, '-'),
        modelName: m.modelName,
        provider: m.provider,
        totalRequests: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalCost: m.cost,
        updatedAt: new Date()
      })));
    } catch (err) {
      console.error('Error refreshing analytics data:', err);
      setError('Failed to refresh analytics data. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
          <p className="text-text-secondary">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <p className="text-text-primary font-medium">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Analytics</h1>
          <p className="text-text-secondary">Track your application performance and user engagement</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            className={cn(
              "p-2 rounded-lg bg-bg-elevated text-text-secondary hover:text-text-primary transition-colors",
              isRefreshing && "animate-spin"
            )}
            title="Refresh data"
            disabled={isRefreshing}
          >
            <RefreshCw size={18} />
          </button>
          <div className="flex items-center gap-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  dateRange === range
                    ? 'bg-accent text-white'
                    : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
                )}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Stats */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <DollarSign className="text-green-500" size={20} />
          Revenue Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-bg-card border border-border-default rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-green-500/10 text-green-500">
                  {stat.icon}
                </div>
                {stat.change !== 0 && (
                  <div className={cn(
                    'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full',
                    stat.trend === 'up' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  )}>
                    {stat.trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(stat.change)}%
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-text-primary">{stat.value}</p>
              <p className="text-sm text-text-secondary">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cost vs Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <h3 className="font-semibold text-text-primary mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-text-secondary">Total Revenue</span>
                <span className="font-medium text-green-500">${revenueStats.totalRevenue.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-bg-elevated rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-text-secondary">API Costs</span>
                <span className="font-medium text-red-400">-${revenueStats.totalApiCost.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-bg-elevated rounded-full overflow-hidden">
                <div className="h-full bg-red-400 rounded-full" style={{ width: `${(revenueStats.totalApiCost / revenueStats.totalRevenue) * 100}%` }} />
              </div>
            </div>
            <div className="pt-2 border-t border-border-default">
              <div className="flex justify-between text-sm">
                <span className="text-text-primary font-medium">Net Profit</span>
                <span className="font-bold text-accent">${revenueStats.profit.toLocaleString()} ({revenueStats.profitMargin.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <h3 className="font-semibold text-text-primary mb-4">Usage by Model (This Month)</h3>
          <div className="space-y-3">
            {aiUsageByModel.length > 0 ? (
              aiUsageByModel.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-primary">{item.model}</span>
                      <span className="text-text-secondary">${item.usage.toFixed(2)}</span>
                    </div>
                    <div className="h-2 bg-bg-elevated rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          index === 0 ? 'bg-green-500' :
                          index === 1 ? 'bg-orange-500' :
                          index === 2 ? 'bg-blue-500' :
                          index === 3 ? 'bg-purple-500' : 'bg-gray-500'
                        )}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-text-tertiary w-10 text-right">{item.percentage}%</span>
                </div>
              ))
            ) : (
              <p className="text-text-tertiary text-center py-4">No AI usage data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Traffic Stats */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Eye className="text-accent" size={20} />
          Traffic Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {trafficStats.map((stat, index) => (
            <div key={index} className="bg-bg-card border border-border-default rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-bg-elevated text-accent">
                  {stat.icon}
                </div>
                {stat.change !== 0 && (
                  <div className={cn(
                    'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full',
                    stat.trend === 'up' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  )}>
                    {stat.trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(stat.change)}%
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-text-primary">{stat.value}</p>
              <p className="text-sm text-text-secondary">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Download Stats by Platform */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <h2 className="font-semibold text-text-primary mb-4">Downloads by Platform</h2>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(downloadStats.byPlatform).map(([platform, count]) => (
            <div key={platform} className="text-center p-4 bg-bg-elevated rounded-lg">
              <p className="text-2xl font-bold text-text-primary">{count.toLocaleString()}</p>
              <p className="text-sm text-text-secondary capitalize">{platform}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Visits Chart */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold text-text-primary">Daily Page Views</h2>
            <div className="flex items-center gap-2 text-sm text-text-tertiary">
              <Calendar size={14} />
              Last 7 days
            </div>
          </div>
          <div className="flex items-end gap-3 h-48">
            {last7DaysStats.length > 0 ? (
              last7DaysStats.map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full relative">
                    <div
                      className="w-full bg-accent/20 rounded-t-lg transition-all hover:bg-accent/30"
                      style={{ height: `${(day.pageViews / maxVisits) * 160}px` }}
                    >
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-accent rounded-t-lg"
                        style={{ height: `${(day.pageViews / maxVisits) * 160 * 0.7}px` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-text-tertiary">{getDayName(day.date)}</span>
                  <span className="text-xs text-text-secondary">{day.pageViews}</span>
                </div>
              ))
            ) : (
              <div className="flex-1 flex items-center justify-center text-text-tertiary">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Traffic by Device */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <h2 className="font-semibold text-text-primary mb-6">Traffic by Device</h2>
          <div className="space-y-4">
            {trafficByDevice.map((device, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-text-tertiary">{getDeviceIcon(device.label)}</span>
                    <span className="text-sm text-text-primary">{device.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-text-secondary">{device.value.toLocaleString()}</span>
                    <span className="text-xs text-text-tertiary">({device.percentage}%)</span>
                  </div>
                </div>
                <div className="h-2 bg-bg-elevated rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      index === 0 ? 'bg-accent' : index === 1 ? 'bg-purple-500' : 'bg-cyan-500'
                    )}
                    style={{ width: `${device.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <h2 className="font-semibold text-text-primary mb-4">Top Pages</h2>
          <div className="space-y-3">
            {topPages.length > 0 ? (
              topPages.map((page, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-border-default last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-bg-elevated flex items-center justify-center text-xs text-text-tertiary">
                      {index + 1}
                    </span>
                    <code className="text-sm text-text-primary">{page.path}</code>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-text-primary">{page.views.toLocaleString()}</p>
                      <p className="text-xs text-text-tertiary">views</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-text-secondary">{page.uniqueVisitors.toLocaleString()}</p>
                      <p className="text-xs text-text-tertiary">unique</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-text-tertiary text-center py-4">No page data available</p>
            )}
          </div>
        </div>

        {/* Traffic by Country */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <h2 className="font-semibold text-text-primary mb-4">Traffic by Country</h2>
          <div className="space-y-3">
            {countryBreakdown.length > 0 ? (
              countryBreakdown.slice(0, 6).map((country, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Globe size={16} className="text-text-tertiary" />
                    <span className="text-sm text-text-primary">{country.country}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-2 bg-bg-elevated rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent rounded-full"
                        style={{ width: `${country.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-text-secondary w-16 text-right">
                      {country.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-text-tertiary text-center py-4">No country data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Daily Stats Table */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <h2 className="font-semibold text-text-primary mb-4">Daily Statistics ({dateRange})</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default">
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Date</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Page Views</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Unique Visitors</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Downloads</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Avg. Session</th>
              </tr>
            </thead>
            <tbody>
              {filteredDailyStats.slice(-10).reverse().map((day, index) => (
                <tr key={index} className="border-b border-border-default/50 hover:bg-bg-elevated/50">
                  <td className="py-3 px-4 text-sm text-text-primary">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </td>
                  <td className="py-3 px-4 text-sm text-text-primary text-right">{day.pageViews.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-text-secondary text-right">{day.uniqueVisitors.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-text-secondary text-right">{day.downloads.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-text-tertiary text-right">
                    {Math.floor(day.avgSessionDuration / 60)}m {day.avgSessionDuration % 60}s
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
